import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button } from 'react-bootstrap';
import Breadcrumbs from '../../../Common/Breadcrumbs';
import { getReturnDetails, approveRejectReturn, initiateRefund, getOrderDetails } from '../../../api/shop/orders/orderService';
import { ReturnDetailData } from '../../../api/shop/types/returnTypes';
import { OrderItemDetail, OrderStatusHistoryItem } from '../../../api/shop/types/orderTypes';
import { getImageUrl } from '../../../utils/imageUrl';
import { formatDate } from '../../../utils/formatUtils';
import secureApi from '../../../utils/secureApi';
import { SHOP_ENDPOINTS } from '../../../api/shop/endpoints';
import ReturnBadge from './components/ReturnBadge';
import TrackOrder from './components/TrackOrder';
import OrderItems from './components/OrderItems';
import ContactInformation from './components/ContactInformation';
import RefundMethod from './components/RefundMethod';
import ApproveRejectModal from './components/ApproveRejectModal';
import ApproveReturnModal from './components/ApproveReturnModal';
import InitiateRefundModal from './components/InitiateRefundModal';
import './OrderDetail.css';

const ReturnDetail: React.FC = () => {
  const { returnId } = useParams<{ returnId: string }>();
  const navigate = useNavigate();
  const [returnDetail, setReturnDetail] = useState<ReturnDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Modal states
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Ref to prevent concurrent API calls
  const isFetchingRef = useRef(false);
  const lastFetchedReturnIdRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchReturnDetail = useCallback(async () => {
    if (!returnId) {
      setError('Missing return ID');
      setLoading(false);
      return;
    }

    // Prevent concurrent calls for the same returnId
    if (isFetchingRef.current && lastFetchedReturnIdRef.current === returnId) {
      return;
    }

    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    try {
      isFetchingRef.current = true;
      lastFetchedReturnIdRef.current = returnId;
      setLoading(true);
      setError(null);
      
      const data = await getReturnDetails(returnId);
      
      // Check if request was aborted
      if (abortController.signal.aborted) {
        return;
      }
      
      setReturnDetail(data);
    } catch (err: any) {
      // Don't set error if request was aborted
      if (abortController.signal.aborted) {
        return;
      }
      
      let errorMessage = 'Failed to load return details';
      if (err.response?.status === 404) {
        errorMessage = 'Return not found. Please check the return ID and try again.';
      } else if (err.response?.status === 403) {
        errorMessage = 'You do not have permission to view this return.';
      } else if (err.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      // Only reset if this is still the current request
      if (lastFetchedReturnIdRef.current === returnId) {
        setLoading(false);
        isFetchingRef.current = false;
        abortControllerRef.current = null;
      }
    }
  }, [returnId]);

  useEffect(() => {
    // Reset refs when returnId changes
    if (lastFetchedReturnIdRef.current !== returnId) {
      isFetchingRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    }
    
    fetchReturnDetail();

    // Cleanup function to abort request on unmount or returnId change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [fetchReturnDetail]); // Only depend on fetchReturnDetail, which already depends on returnId

  const getCurrentStatus = (): string => {
    const status = (returnDetail?.returnDetails?.status || 'REQUESTED').toUpperCase();
    return status;
  };

  // Get status for tracking - considers refund status
  // Returns a status that TrackOrder can use to determine which step to highlight
  const getTrackingStatus = () => {
    const currentStatus = getCurrentStatus();
    const refundStatus = getRefundStatus();
    const hasRefund = !!returnDetail?.returnDetails?.refund;
    
    if (hasRefund && refundStatus) {
      if (refundStatus === 'PROCESSED' || refundStatus === 'COMPLETED' || refundStatus === 'SUCCESS') {
        return 'REFUNDED';
      }
      if (refundStatus === 'INITIATED') {
        return 'REFUND_INITIATED';
      }
    }
    
    return currentStatus;
  };

  const canApproveReject = () => {
    if (!returnDetail) return false;
    const status = getCurrentStatus();
    return status === 'REQUESTED';
  };

  const canInitiateRefund = () => {
    if (!returnDetail) return false;
    const status = getCurrentStatus();
    if (status !== 'APPROVED') return false;
    
    const refund = returnDetail?.returnDetails?.refund;
    return !refund;
  };

  const getRefundStatus = () => {
    const refund = returnDetail?.returnDetails?.refund;
    if (refund?.status) {
      return refund.status.toUpperCase();
    }
    return null;
  };

  // Memoize the payment info handler to prevent unnecessary re-renders
  // Must be before any early returns (React Hooks rules)
  const handleViewPaymentInfo = useCallback(() => {
    if (!returnDetail) return;
    
    const paymentMethodData = returnDetail.paymentMethod;
    let pm = '';
    let ctf = '';
    let ct = '';
    
    if (typeof paymentMethodData === 'string') {
      pm = paymentMethodData;
    } else {
      pm = paymentMethodData.method || '';
      ctf = paymentMethodData.cardLastFour || '';
      ct = paymentMethodData.cardType || '';
    }
    
    alert(`Payment Method: ${pm}\nCard: ${ct || 'N/A'} Ending in XXXX ${ctf || 'N/A'}`);
  }, [returnDetail]);

  const handleApprove = () => {
    setShowApproveModal(true);
  };

  const handleReject = () => {
    setShowRejectModal(true);
  };

  const handleConfirmApprove = async () => {
    if (!returnId) return;
    
    try {
      setIsProcessing(true);
      await approveRejectReturn(
        [returnId], 
        'APPROVED',
        '' // No note required for approve
      );
      
      setShowApproveModal(false);
      await fetchReturnDetail(); // Refresh return data
    } catch (error) {
      alert('Failed to approve return. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmReject = async (note: string) => {
    if (!returnId) return;
    
    try {
      setIsProcessing(true);
      await approveRejectReturn(
        [returnId], 
        'REJECTED',
        note
      );
      
      setShowRejectModal(false);
      await fetchReturnDetail(); // Refresh return data
    } catch (error) {
      alert('Failed to reject return. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleInitiateRefund = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Prevent opening if already open or processing
    if (showRefundModal || isProcessing) {
      return;
    }
    setShowRefundModal(true);
  };

  const handleConfirmRefund = async () => {
    if (!returnId) return;
    
    try {
      setIsProcessing(true);
      await initiateRefund(returnId);
      
      setShowRefundModal(false);
      await fetchReturnDetail(); // Refresh return data
    } catch (error) {
      alert('Failed to initiate refund. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content order-detail-page" style={{ marginTop: '20px' }}>
        <Container fluid>
          {/* Breadcrumbs Shimmer */}
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <div className="shimmer-breadcrumb" style={{ height: '20px', width: '300px', marginBottom: '20px' }} />
          </div>

          {/* Page Header Shimmer */}
          <div style={{ padding: '0 15px', marginBottom: '24px' }}>
            <div className="shimmer-page-header" style={{ height: '40px', width: '200px' }} />
          </div>

          {/* Return Summary Card Shimmer */}
          <div style={{ padding: '0 15px', marginBottom: '32px' }}>
            <div className="shimmer-order-summary-card" style={{ 
              background: 'white', 
              borderRadius: '12px', 
              padding: '24px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
              border: '1px solid rgba(0, 0, 0, 0.05)'
            }}>
              {/* Return Header Shimmer */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div className="shimmer-circle" style={{ width: '40px', height: '40px', borderRadius: '50%' }} />
                  <div>
                    <div className="shimmer-title" style={{ height: '24px', width: '250px', marginBottom: '4px' }} />
                    <div className="shimmer-date" style={{ height: '16px', width: '120px' }} />
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="shimmer-badge" style={{ height: '20px', width: '80px', marginBottom: '8px' }} />
                  <div className="shimmer-order-id" style={{ height: '16px', width: '150px' }} />
                </div>
              </div>

              {/* Track Return Shimmer */}
              <div style={{ 
                borderTop: '1px solid #e9ecef', 
                margin: '0 -24px', 
                padding: '24px',
                background: '#f8f9fa'
              }}>
                <div className="shimmer-track-title" style={{ height: '18px', width: '120px', marginBottom: '24px' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {[1, 2, 3].map((step) => (
                    <div key={step} style={{ textAlign: 'center', flex: 1 }}>
                      <div className="shimmer-circle" style={{ width: '32px', height: '32px', borderRadius: '50%', margin: '0 auto 12px' }} />
                      <div className="shimmer-step-title" style={{ height: '16px', width: '100px', margin: '0 auto 8px' }} />
                      <div className="shimmer-step-date" style={{ height: '14px', width: '120px', margin: '0 auto 4px' }} />
                      <div className="shimmer-step-badge" style={{ height: '16px', width: '70px', margin: '0 auto' }} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Items Section Shimmer */}
          <div style={{ padding: '0 15px', marginBottom: '32px' }}>
            <div className="row">
              {/* Left Column - Items */}
              <div className="col-lg-6">
                <div style={{ 
                  background: 'white', 
                  borderRadius: '12px', 
                  padding: '32px 24px',
                  marginBottom: '24px'
                }}>
                  <div className="shimmer-items-title" style={{ height: '20px', width: '80px', marginBottom: '24px' }} />
                  <div className="shimmer-item-row" style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
                    <div className="shimmer-item-image" style={{ width: '80px', height: '80px', borderRadius: '8px' }} />
                    <div style={{ flex: 1 }}>
                      <div className="shimmer-item-name" style={{ height: '18px', width: '200px', marginBottom: '8px' }} />
                      <div className="shimmer-item-details" style={{ height: '14px', width: '120px' }} />
                    </div>
                    <div className="shimmer-item-price" style={{ height: '18px', width: '80px' }} />
                  </div>
                  <div className="shimmer-total" style={{ height: '20px', width: '150px', marginTop: '20px' }} />
                  <div className="shimmer-payment" style={{ height: '45px', width: '100%', marginTop: '16px', borderRadius: '12px' }} />
                </div>
              </div>

              {/* Right Column - Contact & Return Details */}
              <div className="col-lg-6">
                <div style={{ 
                  background: 'white', 
                  borderRadius: '12px', 
                  padding: '32px 24px',
                  marginBottom: '24px'
                }}>
                  <div className="shimmer-contact-title" style={{ height: '20px', width: '150px', marginBottom: '24px' }} />
                  <div className="shimmer-contact-grid">
                    <div style={{ display: 'flex', gap: '24px', marginBottom: '20px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ height: '14px', width: '100px', marginBottom: '8px' }} />
                        <div style={{ height: '16px', width: '80px' }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ height: '14px', width: '100px', marginBottom: '8px' }} />
                        <div style={{ height: '16px', width: '120px' }} />
                      </div>
                    </div>
                    <div>
                      <div style={{ height: '14px', width: '80px', marginBottom: '8px' }} />
                      <div style={{ height: '16px', width: '200px' }} />
                    </div>
                  </div>
                </div>
                <div style={{ 
                  background: 'white', 
                  borderRadius: '12px', 
                  padding: '32px 24px'
                }}>
                  <div className="shimmer-pickup-title" style={{ height: '20px', width: '150px', marginBottom: '16px' }} />
                  <div className="shimmer-pickup-details" style={{ 
                    height: '120px', 
                    width: '100%', 
                    background: '#EFF6FF', 
                    borderRadius: '12px',
                    padding: '12px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                      <div style={{ width: '24px', height: '24px', borderRadius: '4px' }} />
                      <div>
                        <div style={{ height: '14px', width: '120px', marginBottom: '4px' }} />
                        <div style={{ height: '16px', width: '100px' }} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '24px', height: '24px', borderRadius: '4px' }} />
                      <div>
                        <div style={{ height: '14px', width: '100px', marginBottom: '4px' }} />
                        <div style={{ height: '16px', width: '180px' }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (error || (!loading && !returnDetail)) {
    return (
      <div className="page-content order-detail-page" style={{ marginTop: '20px' }}>
        <Container fluid>
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <Breadcrumbs items={[
              { label: 'Shop Services', onClick: () => navigate('/shop-services/sellers') },
              { label: 'Orders', onClick: () => navigate('/shop-services/orders') },
              { label: 'Return', active: true }
            ]} />
          </div>
          
          <div style={{ padding: '0 15px' }}>
            <div className="alert alert-danger" role="alert">
              <i className="ri-error-warning-line me-2"></i>
              {error || 'Return not found'}
            </div>
          </div>
        </Container>
      </div>
    );
  }

  const orderItemInfo = returnDetail?.orderItemInfo;
  const items = returnDetail?.items || [];
  
  const returnedItem = orderItemInfo ? {
    orderItemId: orderItemInfo.orderItemId || '',
    orderId: orderItemInfo.orderId || '',
    productName: orderItemInfo.productName || '',
    price: orderItemInfo.price || 0,
    totalAmount: orderItemInfo.totalAmount || orderItemInfo.price || 0,
    quantity: orderItemInfo.quantity || 1,
    image: orderItemInfo.image || '',
    color: orderItemInfo.color || '',
    size: orderItemInfo.size || '',
  } : (items.length > 0 ? items[0] : null);
  
  const returnedOrderItemId = orderItemInfo?.orderItemId || returnDetail?.returnedOrderItemId || '';
  
  const totalRefundAmount = returnDetail?.returnDetails?.refund?.amount || 
                            returnedItem?.totalAmount || 
                            returnedItem?.price || 
                            0;
  
  const paymentMethodData = returnDetail?.paymentMethod;
  const refundPaymentDetails = returnDetail?.returnDetails?.refundPaymentDetails;
  
  const rawPaymentMethod = refundPaymentDetails?.paymentMethod || paymentMethodData;
  const actualPaymentMethod = typeof rawPaymentMethod === 'string' 
    ? rawPaymentMethod.toLowerCase().replace(/_/g, '')
    : (typeof rawPaymentMethod === 'object' && rawPaymentMethod !== null 
      ? (rawPaymentMethod.method || '').toLowerCase().replace(/_/g, '')
      : '');
  
  const actualCardLastFour = refundPaymentDetails?.cardLastFour || 
                             (typeof paymentMethodData === 'object' && paymentMethodData !== null 
                               ? (paymentMethodData.cardLastFour || '') 
                               : '');
  const actualCardType = refundPaymentDetails?.cardType || 
                         (typeof paymentMethodData === 'object' && paymentMethodData !== null 
                           ? (paymentMethodData.cardType || '') 
                           : '');
  
  const mappedOrderItem = returnedItem ? {
    productName: returnedItem.productName || '',
    price: returnedItem.price || returnedItem.totalAmount || 0,
    quantity: returnedItem.quantity || 1,
    image: returnedItem.image || '',
    color: returnedItem.color || '',
    seller: returnDetail?.sellerName || '',
    sellerLocation: returnDetail?.sellerLocation || {},
    expectedPickupDate: returnDetail?.returnDetails?.visitDate || null,
    orderStatusHistory: returnDetail?.statusHistory || [],
    isSameItem: true,
    pickupLocation: returnDetail?.returnDetails?.returnLocation?.name || '',
    priceBreakdown: {
      discountAmount: 0,
      grossAmount: returnedItem.price || returnedItem.totalAmount || 0,
      gstAmount: 0,
      gstRate: 18,
      taxableAmount: 0,
      totalAmount: returnedItem.totalAmount || returnedItem.price || 0,
    },
    size: returnedItem.size || 'UK5',
  } as any : null;
  

  return (
    <div className="page-content order-detail-page" style={{ marginTop: '20px' }}>
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={[
            { label: 'Shop Services', onClick: () => navigate('/shop-services/sellers') },
            { label: 'Orders', onClick: () => navigate('/shop-services/orders?tab=RETURN') },
            { label: 'Return', active: true }
          ]} />
        </div>

        {/* Page Header with Back Button */}
        <div style={{ padding: '0 15px', marginBottom: '24px' }}>
          <div className="d-flex align-items-center" style={{ gap: '16px' }}>
            <button 
              className="back-button"
              onClick={() => navigate('/shop-services/orders?tab=RETURN')}
            >
              <i className="ri-arrow-left-line"></i>
            </button>
            <h1 className="page-title mb-0">Return</h1>
          </div>
        </div>

        {/* Return Summary Section - White Card (using same classes as OrderDetail) */}
        <div style={{ padding: '0 15px', marginBottom: '32px' }}>
          <div className="order-summary-card">
            <div className="order-summary-content">
              <div className="order-summary-header">
                <div className="order-summary-left">
                  <div className="order-summary-icon">
                    <img 
                      src={getImageUrl('admin/static-assets/icons/general/icon-returnorder.svg')}
                      alt="Return"
                      style={{ width: '43px', height: '43px' }}
                      loading="lazy"
                    />
                  </div>
                  <div className="order-summary-info">
                    <div className="order-summary-title-row">
                      <h4 className="order-summary-title">
                        {returnedItem?.productName || mappedOrderItem?.productName || 'Return Item'}
                      </h4>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {/* Badge logic based on status and refund state */}
                        <ReturnBadge 
                          returnStatus={getCurrentStatus()} 
                          refundStatus={getRefundStatus() || undefined} 
                        />
                      </div>
                    </div>
                    <p className="order-summary-date">
                      {formatDate(returnDetail?.returnDetails?.statusUpdatedAt || 
                                 (returnDetail?.statusHistory && returnDetail.statusHistory.length > 0 
                                   ? returnDetail.statusHistory[returnDetail.statusHistory.length - 1]?.date 
                                   : undefined) ||
                                 returnDetail?.createdAt)}
                    </p>
                  </div>
                </div>
                <div className="order-summary-right">
                  <div className="order-summary-id">
                    Order ID: <span>{returnDetail?.orderId || ''}</span>
                  </div>
                  <button 
                    className="header-invoice-btn"
                    onClick={async () => {
                      try {
                        const orderItemInfo = returnDetail?.orderItemInfo;
                        const mongoOrderId = orderItemInfo?.orderId || returnDetail?.orderId || '';
                        const mongoOrderItemId = orderItemInfo?.orderItemId || returnDetail?.returnedOrderItemId || '';
                        
                        const isMongoIdFormat = (id: string) => id && /^[0-9a-fA-F]{24}$/.test(id);
                        
                        let finalOrderId = mongoOrderId;
                        let finalOrderItemId = mongoOrderItemId;
                        
                        if (!isMongoIdFormat(mongoOrderId) && mongoOrderId && mongoOrderItemId) {
                          try {
                            const orderDetailsResponse = await getOrderDetails(mongoOrderId, mongoOrderItemId);
                            
                            if (orderDetailsResponse?.data?.orderId && isMongoIdFormat(orderDetailsResponse.data.orderId)) {
                              finalOrderId = orderDetailsResponse.data.orderId;
                            }
                          } catch (err) {
                            alert('Unable to get order details for invoice. Please try again.');
                            return;
                          }
                        }
                        
                        if (!isMongoIdFormat(finalOrderId) || !isMongoIdFormat(finalOrderItemId)) {
                          alert('Invalid order IDs. Unable to download invoice.');
                          return;
                        }
                        
                        const response = await secureApi.get(SHOP_ENDPOINTS.ORDERS.GET_INVOICE(finalOrderId, finalOrderItemId));
                        const invoiceUrl = response?.invoiceUrl || response?.data?.invoiceUrl || response?.url || response?.data?.url || response?.link || response?.data?.link;
                        if (invoiceUrl) {
                          window.open(invoiceUrl, '_blank');
                        } else {
                          const blob = new Blob([response], { type: 'application/pdf' });
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `invoice-${finalOrderId}-${finalOrderItemId}.pdf`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          window.URL.revokeObjectURL(url);
                        }
                      } catch (error) {
                        alert('Error downloading invoice. Please try again.');
                      }
                    }}
                  >
                    Get Invoice
                  </button>
                </div>
              </div>

              {/* Track Return inside the same white card */}
              <div className="order-track-section">
                <TrackOrder 
                  orderStatusHistory={(returnDetail?.statusHistory || []).map((item: any) => ({
                    status: item.status,
                    updatedAt: item.date || item.updatedAt || null // Map 'date' field to 'updatedAt'
                  }))}
                  currentStatus={getTrackingStatus()}
                  expectedPickupDate={returnDetail?.returnDetails?.visitDate}
                  title="Track Return"
                  refundStatus={getRefundStatus()}
                  trackingSteps={(() => {
                    const currentStatus = getCurrentStatus();
                    // If REJECTED, only show REQUESTED and REJECTED steps
                    if (currentStatus === 'REJECTED') {
                      return [
                        {
                          status: 'REQUESTED',
                          title: 'Return Request Submitted',
                          number: '01'
                        },
                        {
                          status: 'REJECTED',
                          title: 'Request Rejected',
                          number: '02'
                        }
                      ];
                    }
                    // Otherwise, show all steps (REQUESTED, APPROVED, REFUNDED)
                    return [
                      {
                        status: 'REQUESTED',
                        title: 'Return Request Submitted',
                        number: '01'
                      },
                      {
                        status: 'APPROVED',
                        title: 'Request approval',
                        number: '02'
                      },
                      {
                        status: 'REFUNDED',
                        title: 'Refund Processed',
                        number: '03'
                      }
                    ];
                  })()}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Return Items Section - Items + Contact Information (same layout as Orders detail) */}
        <div style={{ padding: '0 15px', marginBottom: '32px' }}>
          <Row>
            {/* Left Column - Items */}
            <Col lg={6}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {/* Items Card */}
                {mappedOrderItem && (
                  <OrderItems 
                    orderItems={[mappedOrderItem]}
                    paymentMethod={actualPaymentMethod}
                    sellerName={mappedOrderItem.seller}
                    orderId={orderItemInfo?.orderId || returnDetail?.orderId || ''}
                    orderItemId={orderItemInfo?.orderItemId || returnedItem?.orderItemId || returnedOrderItemId}
                    showViewPaymentInfo={true}
                    onViewPaymentInfo={handleViewPaymentInfo}
                  />
                )}
              </div>
            </Col>
            
            {/* Right Column - Contact Information + Return Details */}
            <Col lg={6}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {/* Contact Information Card */}
                {returnDetail?.customerInfo && (
                  <ContactInformation contact={{
                    name: returnDetail.customerInfo.name || '',
                    email: returnDetail.customerInfo.email || '',
                    phone: returnDetail.customerInfo.phone || '',
                  }} />
                )}
                
                {/* Return Details Card - Matching Contact Information style */}
                <div className="contact-information-container">
                  <h3 className="contact-information-title">Return Details</h3>
                  
                  <div className="contact-info-grid">
                    {/* First row: Pickup date and Reason (if both exist) */}
                    {(returnDetail?.returnDetails?.visitDate || returnDetail?.returnDetails?.reason) && (
                      <div className="contact-info-row">
                        {returnDetail?.returnDetails?.visitDate && (
                          <div className="contact-info-item">
                            <span className="contact-items-label">Pickup date</span>
                            <span className="contact-items-value">
                              {formatDate(returnDetail.returnDetails.visitDate)}
                            </span>
                          </div>
                        )}
                        {returnDetail?.returnDetails?.reason && (
                          <div className="contact-info-item">
                            <span className="contact-items-label">Reason</span>
                            <span className="contact-items-value">
                              {returnDetail.returnDetails.reason}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Second row: Additional details (full width) */}
                    {returnDetail?.returnDetails?.additionalDetails && (
                      <div className="contact-info-row">
                        <div className="contact-info-item full-width">
                          <span className="contact-items-label">Additional details</span>
                          <span className="contact-items-value">
                            {returnDetail.returnDetails.additionalDetails}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Refund Method Section */}
        {totalRefundAmount > 0 && (
          <div style={{ padding: '0 15px', marginBottom: '32px' }}>
            <Row className="justify-content-start">
              <Col lg={6} xs={12} style={{ paddingLeft: '0', paddingRight: '12px' }}>
                <RefundMethod
                  totalRefundAmount={totalRefundAmount}
                  paymentMethod={actualPaymentMethod}
                  cardLastFour={actualCardLastFour}
                  cardType={actualCardType}
                />
              </Col>
            </Row>
          </div>
        )}

        {/* Sticky Action Bar - Match OrderDetail design */}
        {(() => {
          // Calculate button visibility
          const showApproveReject = canApproveReject();
          const showInitiateRefund = canInitiateRefund();
          const showButtons = showApproveReject || showInitiateRefund;
          
          if (!showButtons) return null;
          
          return (
            <div className="order-detail-actions">
              <Container fluid>
                <div className="order-detail-action-bar-content">
                  {showApproveReject ? (
                    <>
                      <Button
                        variant="outline-danger"
                        className="order-detail-action-cancel"
                        onClick={handleReject}
                        disabled={isProcessing}
                      >
                        Reject
                      </Button>
                      <Button
                        variant="primary"
                        className="order-detail-action-confirm"
                        onClick={handleApprove}
                        disabled={isProcessing}
                      >
                        Approve
                      </Button>
                    </>
                  ) : showInitiateRefund ? (
                    <>
                      <Button
                        variant="outline-secondary"
                        className="order-detail-action-cancel"
                        onClick={() => navigate(-1)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        variant="primary"
                        className="order-detail-action-confirm"
                        onClick={handleInitiateRefund}
                        disabled={isProcessing || showRefundModal}
                      >
                        Initiate Refund
                      </Button>
                    </>
                  ) : null}
                </div>
              </Container>
            </div>
          );
        })()}

        {/* Modals */}
        <ApproveReturnModal
          show={showApproveModal}
          handleClose={() => setShowApproveModal(false)}
          handleConfirm={handleConfirmApprove}
          isLoading={isProcessing}
        />
        
        <ApproveRejectModal
          show={showRejectModal}
          handleClose={() => setShowRejectModal(false)}
          handleConfirm={handleConfirmReject}
          actionType="reject"
          isLoading={isProcessing}
        />
        
        <InitiateRefundModal
          show={showRefundModal}
          handleClose={() => setShowRefundModal(false)}
          handleConfirm={handleConfirmRefund}
          refundAmount={totalRefundAmount}
          isLoading={isProcessing}
        />
      </Container>
    </div>
  );
};

export default ReturnDetail;
